import Core from '../core/core.js';
import { default as ModalHelper, ModalSize } from '../helper/modal.js';
import { DomUtility } from '../utilities/domutility.js';
import AsyncLoader from '../helper/async-loader.js';
import { default as ScrollTo, type ScrollToConfig } from '../component/scroll-to.js';
import InitializationManager from '../utilities/initialization-manager.js';
import AttributeToggler from '../component/attribute-toggler.js';
import CslContainer from '../content-element/csl-container.js';

const bootstrap = window.bootstrap;

declare global {
    interface Window {
        ThiemeCore: Core;
    }
}

const initializationManager = new InitializationManager();

interface ToastOptions {
    identifier: string;
    className: string;
    style: string;
    heading: {
        className: string;
    },
    body: {
        className: string;
    },
    content: {
        className: string;
    },
    delay: number
};

const defaultToastOptions: ToastOptions = {
    identifier: '',
    className: '',
    style: '',
    heading: {
        className: 'toast-header',
    },
    body: {
        className: 'toast-body',
    },
    content: {
        className: 'content'
    },
    delay: 0
};


export default class Maintemplate {
    protected isMobile: boolean = false;
    protected screenSize: string = 'xs';
    protected screenSizeInt: number = 0;
    protected stickyTimeout: any;
    protected lang: string = 'en';
    protected toastContainer?: HTMLElement;
    protected loginAlertInfoDefaultContent?: string;

    constructor() {
        this.lang = window.ThiemeCore.Lang.getCurrentLanguage();
        this.init();
    }

    protected init(): void {
        if (document.readyState == 'loading') {
            document.addEventListener('DOMContentLoaded', this.initDom);
        } else {
            this.initDom();
        }
        window.addEventListener('resize', (ev: Event) => {
            this.updateDom();
        });
        window.addEventListener('orientationchange', (ev: Event) => {
            this.updateDom();
        });
        window.addEventListener('message', (ev: MessageEvent) => {
            if(ev.isTrusted && ev.data && ev.data.command) {
                this.handleMessage(ev.data.command, ev.data);
            }
        });

        // Dark Theme
        const colorSchemeMode = window.ThiemeCore.Config.get('colorSchemeMode', '');
        if(window.matchMedia && colorSchemeMode != '') {
            const htmlEl: HTMLElement = document.querySelector('html')!;
            if(colorSchemeMode == 'dark') {
                htmlEl.setAttribute('data-bs-theme', 'dark');
            } else {
                const updateTheme = function(state: boolean = false) {
                    if(state) {
                        htmlEl.setAttribute('data-bs-theme', 'dark');
                    } else {
                        htmlEl.removeAttribute('data-bs-theme');
                    }
                }
                updateTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
                window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', event => {
                    updateTheme(event.matches);
                });
            }
        }
    }

    protected initDom(): void {
        this.updateScreenSize();
        const htmlEl = document.querySelector<HTMLElement>('html');
        if(htmlEl) {
            htmlEl.classList.remove('no-js');
            htmlEl.classList.add('js-enable');
        }

        // Apply Canoncial Link Solution
        const canonicalLinkSolution: number = parseInt(window.ThiemeCore.Config.get('canonicalLinkSolution', '0'));
        if(canonicalLinkSolution > 0) {
            const canonicalLinkEl = document.querySelector('link[rel="canonical"]');
            if(canonicalLinkEl ) {
                if(canonicalLinkSolution > 1) {
                    // Replace attribute
                    canonicalLinkEl.setAttribute('rel', 'canonical-off')
                } else {
                    // Remove element
                    canonicalLinkEl.remove();
                }
            }
        }

        // Move each modal Dialogs to body end
        document.querySelectorAll<HTMLElement>('.modal').forEach((el: HTMLElement) => {
            if (
                el.parentElement!.classList.contains('th-fsc-popup-wrapper')
                || el.parentElement!.classList.contains('th-fsc-popup-wrapper')
            ) {
                document.body.appendChild(el.parentElement!);
            } else {
                document.body.appendChild(el);
            }
        });

        // Move main nav to body end
        const pageNavMainContainer = document.querySelector<HTMLElement>('#th_page_service')
        if(pageNavMainContainer) {
            document.body.appendChild(pageNavMainContainer);
        }

        // Decrypt encrypted content
        document.querySelectorAll<HTMLElement>('span.enc').forEach((el: HTMLElement) => {
            let c = el.dataset.enc ?? '';
            c = c.replace(/\[.*?\]/ig, '');
            c = atob(c);
            el.after(c);
            el.remove();
        });

        // Handle dom update
        document.body.addEventListener('asyncReady', () => {
            this.updateDom();

        });

        // Handle current location anchor
        if (window.location.href.indexOf('#') > -1) {
            const dest: string = window.location.href.substring(window.location.href.indexOf('#'));
            if (dest && dest != '#' && parseInt(dest.substring(0, 1)) < 1 && document.querySelector<HTMLElement>(dest)) {
                this.scrollTo(dest);
            }
        }

        // Service Buttons
        const pageServiceEl: HTMLElement | null = document.querySelector<HTMLElement>('#th_page_service');
        if(pageServiceEl) {
            const pageNavMainContainerOffcanvas: bootstrap.Offcanvas = bootstrap.Offcanvas.getOrCreateInstance(pageServiceEl);

            const menuTabEl: HTMLElement = document.querySelector<HTMLElement>('#th_page_service_tab_menu')!;
            const menuTab: bootstrap.Tab = bootstrap.Tab.getOrCreateInstance(menuTabEl);

            const menuBtnEl: HTMLElement | null = document.querySelector<HTMLElement>('#btn_trigger_th_page_service');
            if(menuBtnEl) {
                menuBtnEl.addEventListener('click', () => {
                    menuTab.show();
                    const firstLinkEl = menuTabEl.querySelector<HTMLElement>('.th-page-nav-main-menu > .nav > .nav-item:first-child > .nav-link');
                    if(firstLinkEl) {
                        firstLinkEl.tabIndex = -1;
                        firstLinkEl.focus();
                    }
                });
            }

            const searchBtnEl = document.querySelector<HTMLElement>('#th_page_nav_search_button');
            const searchTabEl = document.querySelector<HTMLElement>('#th_page_service_tab_search')!;
            if (searchBtnEl && searchTabEl) {
                const searchTab: bootstrap.Tab = bootstrap.Tab.getOrCreateInstance(searchTabEl);
                const searchTabpaneEl = document.querySelector<HTMLElement>('#th_page_service_search_tabpane');
                searchBtnEl.addEventListener('click', (ev: Event) => {
                    ev.preventDefault();
                    const searchTabQueryField: HTMLInputElement = searchTabpaneEl?.querySelector<HTMLInputElement>('input[name="q"]')!;
                    if (!pageServiceEl.classList.contains('show')) {
                        pageNavMainContainerOffcanvas.show();
                        searchTab.show();
                        window.setTimeout(function () {
                            if (searchTabQueryField) {
                                searchTabQueryField.focus();
                            }
                        }, 500);
                    } else {
                        if (searchTabEl.classList.contains('active')) {
                            pageNavMainContainerOffcanvas.show();
                        } else {
                            searchTab.show();
                            window.setTimeout(function () {
                                searchTabQueryField.focus()
                            }, 500);
                        }
                    }
                    return false;
                });
            }

            const userBtnEl = document.querySelector<HTMLElement>('#th_header_service_btn_user');
            if (userBtnEl) {
                const userTabEl = document.querySelector<HTMLElement>('#th_page_service_tab_user')!;
                const userTab = bootstrap.Tab.getOrCreateInstance(userTabEl);
                const userTabpane = document.querySelector<HTMLElement>('#th_page_service_user_tabpane')!;
                userBtnEl.addEventListener('click', (ev: Event) => {
                    ev.preventDefault();
                    const userNameField = userTabpane.querySelector<HTMLInputElement>('input[name="user"]');
                    if (!pageServiceEl.classList.contains('show')) {
                        userTabpane.querySelector('.login-info')!.innerHTML = '';
                        userTabpane.querySelector('.login-info')!.classList.add('d-none');
                        pageNavMainContainerOffcanvas.show();
                        userTab.show();
                        window.setTimeout(function () {
                            if(userNameField) {
                                userNameField.focus();
                            }
                        }, 500);
                    } else {
                        if (userTabEl.classList.contains('active')) {
                            pageNavMainContainerOffcanvas.show();
                        } else {
                            userTab.show();
                            window.setTimeout(function () {
                                if(userNameField) {
                                    userNameField.focus();
                                }
                            }, 500);
                        }
                    }
                });

                // Show user login on paramter promptLogin or on login error
                if (!document.querySelector<HTMLElement>('.th-page-main .login-error')) {
                    if (
                        window.location.href.indexOf('promptLogin=1') > -1
                        || (window.location.href.indexOf('tx_felogin_pi1') > -1 && window.location.href.indexOf('/login/') < 0)
                        || document.querySelector<HTMLElement>('#th_page_service_user_tabpane .login-error')
                    ) {
                        const clickEvent = new Event('click');
                        userBtnEl.dispatchEvent(clickEvent);
                    }
                }
            }

            const accessibilityBtnEl = document.querySelector<HTMLElement>('#th_header_service_btn_accessibility');
            if (accessibilityBtnEl) {
                const accessibilityTabEl = document.querySelector<HTMLElement>('#th_page_service_tab_accessibility')!;
                const accessibilityTab = bootstrap.Tab.getOrCreateInstance(accessibilityTabEl);
                accessibilityBtnEl.addEventListener('click', (ev: Event) => {
                    ev.preventDefault();

                    if (!pageServiceEl.classList.contains('show')) {
                        pageNavMainContainerOffcanvas.show();
                        accessibilityTab.show();
                    } else {
                        if (accessibilityTabEl.classList.contains('active')) {
                            pageNavMainContainerOffcanvas.show();
                        } else {
                            accessibilityTab.show();
                        }
                    }
                    return false;
                });
            }
        }

        // Handle footer overlay nav dropdown
        document.querySelectorAll<HTMLElement>('.th-page-nav-overlay-menu .dropdown-menu').forEach((el: HTMLElement) => {
            const dropdownMenu = bootstrap.Dropdown.getOrCreateInstance(el);
            if (dropdownMenu) {
                if (el.offsetWidth < el.parentElement!.offsetWidth) {
                    el.parentElement!.addEventListener('shown.bs.dropdown', (ev: Event) => {
                        const left = (el.parentElement!.offsetWidth - el.offsetWidth) / 2;
                        el.style.left = left + 'px';
                    });
                }
            }
        });

        // Prepare header meganav
        const megaNav = document.querySelector<HTMLElement>('.th-page-header-meganav');
        if(megaNav) {
            const itemsWrapperEl = megaNav.querySelector<HTMLElement>('.megamenu-items')!;
            if(itemsWrapperEl) {
                const bodyOverflowY = document.body.style.getPropertyValue('overflow-y');
                const updateLayout = () => {
                    const rect = itemsWrapperEl.getBoundingClientRect();
                    const maxHeight = window.innerHeight - rect.top - 45;

                    itemsWrapperEl.querySelectorAll<HTMLElement>('.item-nav, .item-content').forEach((el: HTMLElement) => {
                        el.style.setProperty('max-height', maxHeight + 'px');
                    });

                    document.body.style.setProperty('overflow-y', bodyOverflowY);
                    if(itemsWrapperEl.querySelectorAll('.megamenu-item.show').length > 0) {
                        document.body.style.setProperty('overflow-y', 'hidden');
                    }
                };

                megaNav.querySelectorAll<HTMLElement>('.collapse').forEach((el: HTMLElement) =>  {
                    const navLinkElement = megaNav.querySelector<HTMLElement>('[data-bs-target="#' + el.getAttribute('id') + '"]');
                    if(navLinkElement) {
                        const navItemElement = navLinkElement.closest('.nav-item');
                        if(navItemElement) {
                            el.addEventListener('shown.bs.collapse', (ev: Event) => {
                                navItemElement.classList.add('activated');
                                updateLayout();
                            });
                            el.addEventListener('hidden.bs.collapse', (ev: Event) => {
                                navItemElement.classList.remove('activated');
                                updateLayout();
                            });
                        }
                    }
                });

                document.body.addEventListener('click', (ev: MouseEvent) => {
                    const target = ev.target as HTMLElement;
                    if(!megaNav.contains(target) && megaNav.querySelector('.show')) {
                        megaNav.querySelectorAll<HTMLElement>('.collapse').forEach((el: HTMLElement) =>  {
                            const instance = bootstrap.Collapse.getOrCreateInstance(el, { toggle: false });
                            instance.hide();
                        });
                        updateLayout();
                    }
                });

                window.addEventListener('resize', () => { updateLayout() });
                window.addEventListener('orientationchanged', () => { updateLayout() });
                updateLayout();
            }
        }

        // Init Search
        this.initSearch();

        // Update dom
        this.updateDom();
    }

    // Initilize search
    protected initSearch(): void {
        const searchTabEl = document.querySelector<HTMLElement>('#th_page_service_tab_search');
        if(searchTabEl == null) {
            return;
        }
        let resultsHtml: string = '';
        if (window.sessionStorage) {
            const result = document.querySelector<HTMLElement>('.th-page-main #tx-solr-search,.th-page #tx-indexedsearch')!;

            if (result) {
                const resultsToStore: HTMLElement = result.cloneNode(true) as HTMLElement;
                resultsToStore.querySelectorAll<HTMLElement>('.lazy-container').forEach((el: HTMLElement) => {
                    el.remove();
                });
                if (
                    resultsToStore.querySelector('.results-list')
                    || resultsToStore.querySelector('.tx-indexedsearch-results')
                ) {
                    sessionStorage.setItem('searchResults', resultsToStore.innerHTML);
                } else {
                    sessionStorage.removeItem('searchResults');
                }
            } else {
                resultsHtml = sessionStorage.getItem('searchResults') ?? '';
            }
        }

        const searchTabPaneEl = document.querySelector<HTMLElement>('#th_page_service_search_tabpane');
        if(searchTabPaneEl) {
            const searchTab = bootstrap.Tab.getOrCreateInstance(searchTabEl);
            const defaultSearchForm: HTMLElement|null = searchTabPaneEl.querySelector<HTMLElement>('.search-form');
            const searchResultsWrapper: HTMLElement|null = searchTabPaneEl.querySelector<HTMLElement>('.search-results-wrapper');
            if(defaultSearchForm && searchResultsWrapper) {
                searchResultsWrapper.style.display = 'none';
                defaultSearchForm.style.display = 'block';
                if (resultsHtml && resultsHtml != '') {
                    defaultSearchForm.style.display = 'none';
                    searchResultsWrapper.style.display = 'block';
                    searchTab.show();

                    searchResultsWrapper.innerHTML = resultsHtml;
                }
            }
        }
    }

    protected handleMessage(command: string, data: any) {
        switch(command) {
            case 'showLogin': {
                const message: string = data.message ?? '';
                this.showLogin(message);
                break;
            }
        }
    }

    protected updateCssHelperClasses() {
        document.querySelectorAll<HTMLElement>('.row, .content-wrapper').forEach((rowEl: HTMLElement) =>  {
            const lines: {[key: number]: HTMLElement[]} = {};
            let lineIdx = 0;
            let itemIdx = 0;
            let currentOffsetTop = 0;

            const parentEl: HTMLElement|null = rowEl.parentElement;
            let isRowElLastInParent = false;
            let isRowElFirstInParent = false;
            if(parentEl) {
                let pc = 0;
                for(const childEl of parentEl.children) {
                    pc++;
                    if(childEl === rowEl) {
                        if(pc === 1) {
                            isRowElFirstInParent = true;
                        }
                        if(pc === parentEl.children.length) {
                            isRowElLastInParent = true;
                        }
                    }
                }
            }
            for(const colEl of rowEl.children) {
                if(colEl instanceof HTMLElement) {
                    colEl.classList.remove('first-row-el');
                    colEl.classList.remove('last-row-el');
                    colEl.classList.remove('first-line-el');
                    colEl.classList.remove('last-line-el');
                    colEl.classList.remove('first-inline-el');
                    colEl.classList.remove('last-inline-el');
                    colEl.classList.remove('last-in-parent');
                    colEl.classList.remove('first-in-parent');
                    itemIdx++;
                    if(itemIdx === 1) {
                        colEl.classList.add('first-row-el');
                        if(isRowElFirstInParent) {
                            colEl.classList.add('first-in-parent');
                        }
                    }
                    if(itemIdx === rowEl.children.length) {
                        colEl.classList.add('last-row-el');
                        if(isRowElLastInParent) {
                            colEl.classList.add('last-in-parent');
                        }
                    }
                    if(colEl.offsetTop > currentOffsetTop) {
                        currentOffsetTop = colEl.offsetTop;
                        lineIdx++;
                    }
                    if(typeof(lines[lineIdx]) === 'undefined') {
                        lines[lineIdx] = [];
                    }
                    lines[lineIdx].push(colEl);
                }
            }

            lineIdx = 0;
            const linesCount = Object.keys(lines).length;
            for(const k in lines) {
                lineIdx++;
                const items = lines[k];
                const rowItemsCount = items.length;
                let itemCount = 0;
                items.forEach((el: HTMLElement) => {
                    if(lineIdx === 1) {
                        el.classList.add('first-line-el');
                    }
                    if(lineIdx === linesCount) {
                        el.classList.add('last-line-el');
                    }
                    itemCount++;
                    if(itemCount === 1) {
                        el.classList.add('first-inline-el');
                    }
                    if(itemCount === rowItemsCount) {
                        el.classList.add('last-inline-el');
                    }
                })
            }
        });
    }

    protected updateScreenSize() {
        this.isMobile = true;
        const htmlEl = document.querySelector<HTMLElement>('html') as HTMLElement;
        htmlEl.classList.remove('screen-xxs');
        htmlEl.classList.remove('screen-xs');
        htmlEl.classList.remove('screen-sm');
        htmlEl.classList.remove('screen-md');
        htmlEl.classList.remove('screen-lg');
        htmlEl.classList.remove('screen-xl');

        const windowWidth: number = window.screen.width;
        let screenSize: string = 'xs';
        let screenSizeInt: number = 0;
        const dpiHelper = document.createElement('div');
        dpiHelper.setAttribute('style', 'height: 1in; left: -100%; position: absolute; top: -100%; width: 1in;');
        document.body.append(dpiHelper);
        let dpi = dpiHelper.offsetHeight;
        if(dpi < 72) {
            dpi = 72;
        }
        dpiHelper.remove();

        if(windowWidth >= 576) {
            screenSize = 'sm';
            screenSizeInt = 1;
            this.isMobile = false;
        }
        if(windowWidth >= 768) {
            screenSize = 'md';
            screenSizeInt = 2;
        }
        if(windowWidth >= 992) {
            screenSize = 'lg';
            screenSizeInt = 3;
        }
        if(windowWidth >= 1200) {
            screenSize = 'xl';
            screenSizeInt = 4;
        }
        if(windowWidth >= 1400) {
            screenSize = 'xxl';
            screenSizeInt = 5;
        }
        if(windowWidth >= 3840) {
            screenSize = '4k';
            screenSizeInt = 6;
        }
        if(windowWidth >= 7680) {
            screenSize = '8k';
            screenSizeInt = 7;
        }

        htmlEl.classList.add('screen-' + screenSize);
        htmlEl.classList.add('screen-dpi-' + dpi);
        if(windowWidth < 480) {
            htmlEl.classList.add('screen-xxs');
            screenSize = 'xxs';
            screenSizeInt = -1;
        }

        this.screenSize = screenSize;
        this.screenSizeInt = screenSizeInt;
    }

    // Prepare medias
    protected prepareMedias() {
        document.querySelectorAll<HTMLIFrameElement>('iframe.vsh-player[data-chapters]').forEach((iframeEl: HTMLIFrameElement) => {
            if(initializationManager.hasElement('iframe', iframeEl)) {
                return;
            }

            type Options = {
                hideCuepoint: boolean;
            };

            type Chapter = {
                text: string;
                seconds: number;
                cuepoint: string;
            };

            type Chapters = {
                options: Options;
                items: Array<Chapter>;
            };

            const chapters: Chapters = JSON.parse(decodeURIComponent(iframeEl.dataset.chapters!));
            const options: Options = chapters.options;
            const items: Array<Chapter> = chapters.items;

            const listWrapperEl: HTMLElement = document.createElement('div');
            listWrapperEl.setAttribute('class', 'media-chapters-wrapper');

            const listGroupEl: HTMLElement = document.createElement('div');
            listGroupEl.setAttribute('class', 'media-chapters list-group');

            listWrapperEl.appendChild(listGroupEl);

            items.forEach((chapter) => {
                const listGroupItemEl: HTMLElement = document.createElement('a');
                listGroupItemEl.setAttribute('class', 'list-group-item list-group-item-action');
                listGroupItemEl.addEventListener('click', (ev: Event) => {
                    ev.preventDefault();
                    iframeEl.contentWindow?.postMessage({
                        auftrag: 'spuleZuSek',
                        inhalt: chapter.seconds
                    }, '*');
                    return false;
                });

                const listGroupItemContentEl = document.createElement('div');
                listGroupItemContentEl.setAttribute('class', 'row align-items-baseline');
                listGroupItemContentEl.innerHTML += '<div class="col-8"><span class="title">'+chapter.text+'</span></div>';
                if(options.hideCuepoint) {
                    listGroupItemContentEl.innerHTML += '<div class="col-4 text-end text-small"><span class="cuepoint">'+chapter.cuepoint+'</span>';
                }
                listGroupItemEl.appendChild(listGroupItemContentEl);
                listGroupEl.appendChild(listGroupItemEl);
            });

            iframeEl.closest('figure')?.after(listWrapperEl);

            initializationManager.addElement('iframe', iframeEl);
        });

        // Handle media seeks and chapter links
        document.querySelectorAll<HTMLElement>('*[data-toggle="media-chapter"],*[data-toggle="media-seek"]').forEach((el: HTMLElement) => {
            if(initializationManager.hasElement('media-chapters', el)) {
                return;
            }

            el.addEventListener('click', (ev) => {
                ev.preventDefault();
                let seconds: number = parseInt(el.dataset.seconds!);
                const cuePoint = el.dataset.cuepoint;
                if(typeof(cuePoint) != 'undefined') {
                    const timeParts = cuePoint.split(':');
                    let dt: Date = new Date();
                    dt.setHours(parseInt(timeParts[0]));
                    dt.setMinutes(parseInt(timeParts[1]));
                    dt.setSeconds(parseInt(timeParts[2]));
                    const startTs = dt.getTime();
                    dt = new Date();
                    dt.setHours(0);
                    dt.setMinutes(0);
                    dt.setSeconds(0);

                    seconds = (startTs-dt.getTime())/1000;
                }

                let playerEl: HTMLElement | null;

                if(el.dataset.bsTarget) {
                    playerEl = document.querySelector<HTMLElement>(el.dataset.bsTarget);
                } else {
                    playerEl = el.closest('.ce-textmedia,.article')!.querySelector('iframe.vsh-player,video,audio');
                }

                if(typeof(seconds) != 'undefined' && playerEl) {
                    switch(playerEl.constructor) {
                        case HTMLIFrameElement:
                            (playerEl as HTMLIFrameElement).contentWindow?.postMessage({
                                auftrag: 'spuleZuSek',
                                inhalt: seconds
                            }, '*');
                        break;
                        case HTMLVideoElement:
                            (playerEl as HTMLVideoElement).currentTime = seconds;
                        break;
                        case HTMLAudioElement:
                            (playerEl as HTMLAudioElement).currentTime = seconds;
                        break;
                    }

                    this.scrollTo(playerEl);
                }

                return false;
            });

            initializationManager.addElement('media-chapters', el);
        });
    }

    public updateLayouts() {
        // Adjust height of elements
        document.querySelectorAll<HTMLElement>('[data-adjust-height-of]').forEach((el: HTMLElement) => {
            this.adjustHeight('.tab-content', el.dataset.adjustHeightOf!, null, el.parentElement);
        });
        this.adjustHeight('.tab-content', '.tab-pane', '.frame,.card', document.querySelectorAll<HTMLElement>('.th-fsc-tab[data-adjust-height]'));
        this.adjustHeight('.carousel-inner', '.carousel-item', null, document.querySelectorAll<HTMLElement>('.carousel[data-adjust-height]'));
        this.adjustHeight('.row', '.th-fsc-cc-col', '.frame,.card', document.querySelectorAll<HTMLElement>('.th-fsc-cc[data-adjust-height]'));
        this.adjustHeight('.adjust-children-height', '*', '', document.querySelector<HTMLElement>('body'), function(minHeight: number, groupSelector: string) {
            document.querySelectorAll<HTMLElement>(groupSelector).forEach((childEl: HTMLElement) => {
                childEl.style.height = minHeight+'px';
            });
        });
        this.adjustHeight(
            '.row[data-adjust-height]',
            '.col > .article'
        );

        // Prepare Ads
        document.querySelectorAll<HTMLElement>('.th-page-main .th-asset-ad .ad-content').forEach((el: HTMLElement) => {
            if(el.offsetHeight > 0 && !el.classList.contains('my-4')) {
                el.classList.add('my-4');
            }
        });

        // Adjust footer overlay spacer
        const footerOverlayEl: HTMLElement = document.querySelector<HTMLElement>('.th-page-footer-overlay')!;
        const pageWrapperEl: HTMLElement = document.querySelector<HTMLElement>('.th-page-wrapper')!;
        if(footerOverlayEl) {
            pageWrapperEl.style.paddingBottom = ( footerOverlayEl.offsetHeight + 20) + 'px';
        }
    }

    public toast(title: string = '', content: string = '', opt: {[key: string]: any}): void {
        const options:ToastOptions = { ...defaultToastOptions, ...opt };

        // Create toast container
        if(this.toastContainer == null) {
            this.toastContainer = document.createElement('div');
            this.toastContainer.classList.add('th-page-toast-container');
            //const pageMainEl: HTMLElement|null = document.querySelector<HTMLElement>('.th-page-main');
            //this.toastContainer.style.top = (pageMainEl != null) ? pageMainEl.offsetTop + 'px' : '100px';
            const pageFooterOverlayEl: HTMLElement|null = document.querySelector<HTMLElement>('.th-page-footer-overlay');
            this.toastContainer.style.bottom = (pageFooterOverlayEl && pageFooterOverlayEl.style.getPropertyValue('display') !== 'none') ? (pageFooterOverlayEl.offsetHeight) + 'px' : '0px';

            document.body.appendChild(this.toastContainer);
        }


        if(options.identifier != '') {
            if(document.querySelector('#'+options.identifier) != null) {
                document.querySelector('#'+options.identifier)?.remove();
            }
        }

        const toastEl = document.createElement('div');
        toastEl.setAttribute('id', options.identifier);
        toastEl.setAttribute('role', 'alert');
        toastEl.classList.add('toast');
        if(options.className && options.className != '') {
            toastEl.classList.add(...options.className.split(/ /));
        }
        if(options.style && options.style != '') {
            toastEl.setAttribute('style', options.style);
        }

        const toastHeadingEl = document.createElement('div');
        if(options.heading.className && options.heading.className != '') {
            toastHeadingEl.classList.add(...options.heading.className.split(/ /));
        }
        toastHeadingEl.innerHTML = `
            <strong class="flex-fill">${title}</strong>
            <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
        `;
        toastEl.append(toastHeadingEl);

        const bodyEl = document.createElement('div');
        if(options.body.className && options.body.className != '') {
            bodyEl.classList.add(...options.body.className.split(/ /));
        }

        const contentEl = document.createElement('div');
        if(options.content.className && options.content.className != '') {
            contentEl.classList.add(...options.content.className.split(/ /));
        }
        contentEl.innerHTML = content;

        bodyEl.append(contentEl);
        toastEl.append(bodyEl);

        this.toastContainer.append(toastEl);

        let toastOptions = {
            animation: true,
            delay: options.delay,
            autohide: true
        };

        if(options.delay < 1) {
            toastOptions.autohide = false;
        }

        const toastInstance = bootstrap.Toast.getOrCreateInstance(toastEl, toastOptions);

        toastEl.addEventListener('hidden.bs.toast', () => {
            toastEl.remove();
        });

        toastInstance.show();
    }

    public adjust(
        mode: string,
        groupSelector: string,
        itemSelector: string,
        alternateSubItemSelector: string|null = null,
        context: HTMLElement|NodeListOf<HTMLElement>|null = null,
        callBack: Function|null = null
    ): void {
        const adjust = function(mode: string, groupSelector: string, itemSelector: string, alternateSubItemSelector: string | null = null, context: HTMLElement, callBack: Function | null = null) {
            const elements = context.querySelectorAll<HTMLElement>(groupSelector);
            elements.forEach((groupEl) => {
                let min = 0;
                let groupedByOffsetTop: {
                    [key: number]: {
                        items: HTMLElement[],
                        min: number
                    }
                } = {};

                let groupedMin = 0;
                let items: HTMLElement[] = [];
                groupEl.querySelectorAll<HTMLElement>(itemSelector).forEach((subEl: HTMLElement) => {
                    let item: HTMLElement = subEl;
                    if (
                        alternateSubItemSelector
                        && alternateSubItemSelector != ''
                        && subEl.querySelector(alternateSubItemSelector)
                    ) {
                        item = subEl.querySelector<HTMLElement>(alternateSubItemSelector)!;
                    }
                    if(item) {
                        if(mode == 'width') {
                            item.style.minWidth = '';
                        } else {
                            item.style.minHeight = '';
                        }
                        items.push(item);
                    }
                });
                items.forEach((subEl: HTMLElement) => {
                    let item: HTMLElement = subEl;
                    if (
                        alternateSubItemSelector
                        && alternateSubItemSelector != ''
                        && subEl.querySelector(alternateSubItemSelector)
                    ) {
                        item = subEl.querySelector<HTMLElement>(alternateSubItemSelector)!;
                    }

                    let ot: number = item.offsetTop;
                    let es: number = (mode == 'width') ? item.offsetWidth : item.offsetHeight;
                    let temp: HTMLElement = item.cloneNode(true) as HTMLElement;
                    Object.assign(temp.style, {
                        overflow: 'auto',
                        height: 'auto',
                        maxHeight: 'none',
                        opacity: '0',
                        visibility: 'hidden',
                        display: 'block',
                        minHeight: '',
                    });

                    item.after(temp);

                    const styles = window.getComputedStyle(item);
                    let margin = parseFloat(styles['marginTop']) + parseFloat(styles['marginBottom']);
                    es = temp.offsetHeight + margin;
                    if(mode == 'width') {
                        margin = parseFloat(styles['marginLeft']) + parseFloat(styles['marginRight']);
                        es = temp.offsetHeight + margin;
                    }
                    temp.remove();

                    if (typeof (groupedByOffsetTop[ot]) == 'undefined') {
                        groupedByOffsetTop[ot] = {
                            items: [],
                            min: 0
                        };
                        groupedMin = 0;
                    }

                    if (es > min) {
                        min = es;
                    }

                    if (es > groupedMin) {
                        groupedMin = es;
                    }

                    groupedByOffsetTop[ot].items.push(item);
                    groupedByOffsetTop[ot].min = groupedMin;
                });

                for (const k in groupedByOffsetTop) {
                    let group = groupedByOffsetTop[k];
                    group.items.forEach((item: HTMLElement) => {
                        if (
                            alternateSubItemSelector
                            && alternateSubItemSelector != ''
                            && item.querySelector<HTMLElement>(alternateSubItemSelector)
                        ) {
                            item = item.querySelector<HTMLElement>(alternateSubItemSelector)!;
                        }

                        let size = (groupEl.classList.contains('row')) ? group.min : min;
                        if (size > 0) {
                            if(mode == 'width') {
                                item.style.minWidth = size + 'px';
                            } else {
                                item.style.minHeight = size + 'px';
                            }
                        }

                        if (typeof (callBack) == 'function') {
                            callBack(min, groupSelector, itemSelector, alternateSubItemSelector, context);
                        }
                    });
                }
            });
        }

        let contextElements: HTMLElement[] = [];

        if(context instanceof HTMLElement) {
            contextElements.push(context);
        } else if(context instanceof NodeList) {
            context.forEach((contextEl: HTMLElement) => {
                contextElements.push(contextEl);
            });
        } else if(context === null) {
            contextElements.push(document.body);
        }

        if(contextElements.length > 0) {
            contextElements.forEach((contextEl) => {
                DomUtility.imagesLoaded(contextEl).then(() => {
                    adjust(mode, groupSelector, itemSelector, null, contextEl, callBack);
                });
            });
        }
    }

    public adjustWidth(
        groupSelector: string,
        itemSelector: string,
        alternateSubItemSelector: string|null = null,
        context: HTMLElement|NodeListOf<HTMLElement>|null = null,
        callBack: Function|null = null
    ): void {
        this.adjust('width', groupSelector, itemSelector, alternateSubItemSelector, context, callBack);
    }

    public adjustHeight(
        groupSelector: string,
        itemSelector: string,
        alternateSubItemSelector: string|null = null,
        context: HTMLElement|NodeListOf<HTMLElement>|null = null,
        callBack: Function|null = null
    ): void {
        this.adjust('height', groupSelector, itemSelector, alternateSubItemSelector, context, callBack);
    }

    public updateDom(): void {
        this.updateLayouts();
        this.updateScreenSize();
        this.updateCssHelperClasses();

        // Switch header position to fixed
        const pageHeaderEl = document.querySelector<HTMLElement>('.th-page-header');
        if(pageHeaderEl) {
            //pageHeaderEl.classList.add('position-fixed');
            const asideContentEl = document.querySelector<HTMLElement>('.th-page-aside .aside-content');
            if(asideContentEl) {
                //asideContentEl.classList.add('position-fixed');
            }
        }

        // Toast
        if(this.toastContainer) {
            const pageMainEl: HTMLElement|null = document.querySelector<HTMLElement>('.th-page-main');
            this.toastContainer.style.top = (pageMainEl != null) ? pageMainEl.offsetTop+'px' : '100px';
        }

        // Prepare images which have a link data attribute
        document.querySelectorAll<HTMLElement>('img[data-link]').forEach((el: HTMLElement) =>  {
            if(el.dataset.isImageLinkInitialized) {
                return;
            }
            el.style.cursor = 'pointer';
            el.addEventListener('click', (ev: Event) => {
                let link = el.dataset.link ?? '';
                let target = '';
                if(link.search('_blank') > -1) {
                    link = link.substring(0, link.search('_blank'));
                    target = '_blank';
                }
                if(target != '') {
                    window.open(link, target);

                } else {
                    window.location.href = link;
                }
            });

            el.dataset.isImageLinkInitialized = 'true';
        });

        // Nav fit
        document.querySelectorAll<HTMLElement>('.nav-autocut,.nav-fit').forEach((navEl: HTMLElement) => {
            if(navEl.style.display.search('flex') > -1 && navEl.style.flexDirection === 'column') {
                return;
            }
            if(initializationManager.hasElement('nav-autocut', navEl)) {
                return;
            }

            let currentWindowWidth = 0;
            const initialWidth: string = navEl.style.width;
            const initialOverflow: string = navEl.style.overflow;

            const splitItemsDropdownEl = document.createElement('ul');
            splitItemsDropdownEl.classList.add('dropdown-menu');

            const splitButtonEl: HTMLElement = document.createElement('a');
            splitButtonEl.classList.add('nav-link');
            splitButtonEl.setAttribute('href', '#');
            splitButtonEl.setAttribute('data-bs-toggle', 'dropdown');
            splitButtonEl.setAttribute('data-bs-config', '{ "autoClose": "outside" }');
            splitButtonEl.innerHTML = '<span class="bi bi-three-dots icon-lg"></span><span class="sr-only">Mehr...</span>';

            let splitItemEl: HTMLElement|null;
            const updateVisual = () => {
                if(window.screen.width === currentWindowWidth) {
                    return;
                }
                currentWindowWidth = window.screen.width;
                navEl.style.width = initialWidth;
                navEl.style.overflow = initialOverflow;
                navEl.classList.remove('cutted');
                if(splitItemsDropdownEl.children.length > 0) {
                    for(const item of splitItemsDropdownEl.children) {
                        navEl.append(item);
                    }
                    if(splitItemEl != null) {
                        splitItemEl.remove();
                    }
                    splitItemEl = null;
                }
                navEl.style.width = navEl.offsetWidth + 'px';
                navEl.style.overflow = 'hidden';

                let firstElementTopPosition: number;
                let navWidthRest = navEl.offsetWidth;
                let splitItemsCount = 0;
                const navItems = navEl.querySelectorAll('li');
                navItems.forEach((itemEl: HTMLElement) => {
                    if(typeof(firstElementTopPosition) === 'undefined') {
                        firstElementTopPosition = itemEl.offsetTop;
                    }
                    if(itemEl.offsetTop > firstElementTopPosition) {
                        splitItemsCount++;
                    }
                });

                if(splitItemsCount > 0) {
                    const splitItems: HTMLElement[] = [];
                    navItems.forEach((itemEl: HTMLElement) => {
                        navWidthRest -= itemEl.offsetWidth;
                        if(itemEl.offsetTop > firstElementTopPosition || (navWidthRest - 60) < 0) {
                            splitItems.push(itemEl);
                        }
                    });

                    splitItems.forEach((itemEl: HTMLElement) => {
                        if(itemEl.classList.contains('dropdown')) {
                            itemEl.classList.remove('dropdown');
                            itemEl.classList.add('dropend');
                        }
                        splitItemsDropdownEl.append(itemEl);
                    });

                    splitItemEl = document.createElement('li');
                    splitItemEl.classList.add('nav-item', 'dropdown', 'nav-item-split');
                    splitItemEl.appendChild(splitButtonEl);
                    splitItemEl.appendChild(splitItemsDropdownEl);

                    navEl.append(splitItemEl);
                    navEl.classList.add('cutted');
                    navEl.style.overflow = 'visible';
                }

                navEl.style.overflow = initialOverflow;
            };

            window.addEventListener('resize', updateVisual);
            window.addEventListener('orientationchange', updateVisual);

            updateVisual();

            initializationManager.addElement('nav-autocut', navEl);
        });

        // Show user Login on restricted page link
        if(parseInt(window.ThiemeCore.Config.get('enableRestrictedPageLinkHandler', 0)) > 0) {
            document.querySelectorAll<HTMLElement>('a,.btn-show-login').forEach((el: HTMLElement) => {
                if(initializationManager.hasElement('show-login', el)) {
                    return;
                }
                if(
                    el.getAttribute('href') && el.getAttribute('href')!.search('#restricted-page') > -1
                    || el.dataset.loginRequired && parseInt(el.dataset.loginRequired) > 0
                    || el.dataset.login && parseInt(el.dataset.login) > 0
                    || el.classList.contains('btn-show-login')
                ) {
                    el.addEventListener('click', (ev: MouseEvent) => {
                        ev.preventDefault();
                        const dropdownMenuEl = (ev.target as HTMLElement).closest('.dropdown-menu');
                        if(dropdownMenuEl) {
                            const dropdownLinkEl = dropdownMenuEl.parentElement?.querySelector('[data-bs-target="#'+dropdownMenuEl.getAttribute('id')+'"]');
                            if(dropdownLinkEl) {
                                const dropdown = bootstrap.Dropdown.getOrCreateInstance(dropdownLinkEl);
                                if(dropdown) {
                                    dropdown.hide();
                                }
                            }
                        }
                        let msg = window.ThiemeCore.Lang.translate('login.required');
                        if(el.dataset.message) {
                            msg = el.dataset.message;
                        }
                        let href = '';
                        if(el.getAttribute('href') && el.getAttribute('href') != '#') {
                            href = el.getAttribute('href')!;
                        }
                        this.showLogin(msg, href);

                        return false;
                    });
                    initializationManager.addElement('show-login', el);

                }
            });
        }

        // Prepare buttons or links to show login
        document.querySelectorAll<HTMLElement>('[data-action="login"],[data-toggle="login"],[href="#showLogin"]').forEach((el: HTMLElement) =>  {
            if(initializationManager.hasElement('show-login', el)) {
                return;
            }

            el.addEventListener('click', (ev: MouseEvent) => {
                ev.preventDefault();
                let msg: string = '';
                if(el.dataset.message) {
                    msg = el.dataset.message;
                }
                let href: string = '';
                if(el.getAttribute('href') != '#' && el.getAttribute('href') != '#showLogin') {
                    href = el.getAttribute('href') ?? '';
                }

                this.showLogin(msg, href);
            });

            initializationManager.addElement('show-login', el);

        });

        // Handle anchor links
        document.querySelectorAll<HTMLElement>('.anchor,[data-anchor]').forEach((el: HTMLElement) => {
            const targetSel = el.dataset['target'] ?? el.getAttribute('href') ?? '';
            if(targetSel !== '') {
                const instance = ScrollTo.getOrCreateInstance(el, {
                    target: targetSel
                });
                instance.process();
            }
        });

        // Javascript Window
        document.querySelectorAll<HTMLElement>('a[target="window"]').forEach((el: HTMLElement) => {
            if(!this.isMobile) {
                if(initializationManager.hasElement('target-window', el)) {
                    return;
                }
                el.addEventListener('click', (ev: Event) => {
                    window.open(el.getAttribute('href')!, '', 'menubar=yes,width=800,height=600')
                    return false;
                });
                initializationManager.addElement('target-window', el);

            }
        });

        // Modal handling
        document.querySelectorAll<HTMLElement>('a[target="_popup"],a[target="_modal"],a[data-target="popup"],a[data-target="modal"],a[data-bs-target="popup"],a[data-bs-target="modal"]').forEach((el: HTMLElement) => {
            if(initializationManager.hasElement('modal-toggle', el)) {
                return;
            }
            el.addEventListener('click', (ev: Event) => {
                ev.preventDefault();
                const title = el.getAttribute('title') ?? el.dataset.popupTitle ?? '';
                const iframeEl: HTMLIFrameElement = document.createElement('iframe');
                iframeEl.setAttribute('src', el.getAttribute('href')!);
                iframeEl.setAttribute('frameborder', '0');
                iframeEl.setAttribute('scrolling', 'auto');
                iframeEl.setAttribute('loading', 'lazy');
                iframeEl.style.width = '100%';
                ModalHelper.show(
                    title,
                    iframeEl,
                    ModalSize.XL,
                    parseInt(el.dataset.popupWidth!),
                    parseInt(el.dataset.popupHeight!),
                    parseInt(el.dataset.popupBackdrop!) > 0 ? true : false
                );
                return false;
            });
            initializationManager.addElement('modal-toggle', el)
        });

        // Async Loading
        document.querySelectorAll<HTMLElement>('[data-async="true"]').forEach((el: HTMLElement) => {
            if(initializationManager.hasElement('async-loading', el)) {
                return;
            }
            AsyncLoader.load(el);
            initializationManager.addElement('async-loading', el);
        });

        // Prepare reference links
        document.querySelectorAll<HTMLElement>('.th-page-main').forEach((parentEl: HTMLElement) => {
            parentEl.querySelectorAll<HTMLElement>('ol.reference-list-auto').forEach((referenceList: HTMLElement, idx) => {
                if(initializationManager.hasElement('reference-links', referenceList)) {
                    return;
                }

                let mode = 'popover';
                if(referenceList.classList.contains('reference-mode-anchor')) {
                    mode = 'anchor';
                }

                if(referenceList.children.length > 0) {
                    const replaceRecursive = function(item: HTMLElement) {
                        if(
                            !item
                            || item === referenceList
                            || item.closest('.reference-list-auto') !== null
                            || initializationManager.hasElement('reference-links-prepared-items', item)
                        ) {
                            return;
                        }

                        if(item.children && item.children.length > 0) {
                            for(let i = 0; i < item.children.length; i++) {
                                replaceRecursive(item.children[i] as HTMLElement);
                            }
                        }

                        if(item.nodeType === 1) {
                            item.querySelectorAll<HTMLElement>('sup').forEach((el: HTMLElement) => {
                                let links: String[] = [];
                                let digits = el.textContent.split(',');
                                for(const k in digits) {
                                    const itemIndex = parseInt(digits[k]);
                                    if(itemIndex > 0 && referenceList.children.item((itemIndex - 1))) {
                                        links.push('<a href="#" class="autoref-link" data-refindex="' + (itemIndex - 1) + '">' + itemIndex + '</a>');
                                    }
                                }
                                if(links.length > 0) {
                                    el.innerHTML = links.join(', ');
                                }
                            });

                            if(item.textContent.match(/\[\d.*?\]/ig)) {
                                item.innerHTML = item.innerHTML.replace(/\>.*?</ig, (tagContent: string) => {
                                    tagContent = tagContent.replace(/\[\d.*?\]/ig, (m: any) => {
                                        const matches = m.match(/(\[)(.*?)(\])/i);
                                        if(matches[2] && matches[0].indexOf('[[') < 0) {
                                            let links: String[] = [];
                                            let digits = matches[2].split(',');
                                            for(const k in digits) {
                                                const itemIndex = parseInt(digits[k]);
                                                if(itemIndex > 0 && referenceList.children.item((itemIndex - 1))) {
                                                    links.push('<a href="#" class="autoref-link" data-refindex="' + (itemIndex - 1) + '">'+itemIndex+'</a>');
                                                }
                                            }
                                            if(links.length > 0) {
                                                return matches[1] + links.join(', ') + matches[3];
                                            }
                                            return matches[0];
                                        }
                                        return null;
                                    });

                                    return tagContent;
                                });
                            }
                        }

                        initializationManager.addElement('reference-links-prepared-items', item);
                    }

                    replaceRecursive(parentEl);

                    parentEl.querySelectorAll<HTMLElement>('.autoref-link').forEach((el: HTMLElement) => {
                        const refindex: number = (el.dataset.refindex) ? parseInt(el.dataset.refindex) : 0;
                        const referenceList: HTMLElement = parentEl.querySelectorAll<HTMLElement>('ol.reference-list-auto')[idx] as HTMLElement;
                        const targetElement: HTMLElement = referenceList.children[refindex] as HTMLElement;
                        el.removeAttribute('href');

                        if(targetElement) {
                            switch(mode) {
                                case 'popover':
                                    bootstrap.Popover.getOrCreateInstance(el, {
                                        trigger: 'click hover',
                                        placement: 'bottom',
                                        html: true,
                                        content: targetElement.innerHTML
                                    });
                                break;
                                default:
                                    el.addEventListener('click', function(ev: Event) {
                                        window.scrollTo(0, targetElement.offsetTop);
                                        ev.preventDefault();
                                    });
                                break;
                            }
                        }
                    });
                }

                initializationManager.addElement('reference-list', referenceList);
            });
        });


        // Enable tooltips and popover
        if (typeof (bootstrap.Popover) != 'undefined') {
            document.querySelectorAll<HTMLElement>('[data-toggle="popover"],[data-bs-toggle="popover"]').forEach((el: HTMLElement) => {
                el.style.cursor = 'pointer';

                bootstrap.Popover.getOrCreateInstance(el, {
                    content: el.dataset.bsContent || el.dataset.content || '',
                    placement: 'auto',
                    trigger: (el.dataset.bsTrigger || el.dataset.trigger || 'click') as any
                });
            });

            // Abbr
            document.querySelectorAll<HTMLElement>('abbr').forEach((el: HTMLElement) => {
                el.classList.add('custom');
                el.style.cursor = 'pointer';
                let timeout: number;
                const content = el.getAttribute('title') ?? '';
                el.removeAttribute('title');
                const popover = bootstrap.Popover.getOrCreateInstance(el, {
                    content: content,
                    placement: 'bottom',
                    trigger: 'hover',
                    html: true
                });
                el.addEventListener('click', () => {
                    popover.toggle();
                });
                el.addEventListener('hidden.bs.popover', () => {
                    window.clearTimeout(timeout);
                });
                el.addEventListener('shown.bs.popover', () => {
                    timeout = window.setTimeout(function () {
                        popover.hide();
                    }, 10000);
                    window.clearTimeout(timeout);
                });
            });
        } else {
            // Abbr
            document.querySelectorAll<HTMLElement>('abbr').forEach((el: HTMLElement) => {
                let timeout: number;
                el.addEventListener('click', () => {
                    el.classList.toggle('show');
                    if (el.classList.contains('show')) {
                        timeout = window.setTimeout(function () {
                            el.classList.remove('show');
                        }, 10000);
                    } else {
                        window.clearTimeout(timeout);
                    }
                });
            });

        }

        if (typeof (bootstrap.Tooltip) != 'undefined') {
            document.querySelectorAll<HTMLElement>('[data-toggle="tooltip"],[data-bs-toggle="tooltip"]').forEach((el: HTMLElement) => {
                bootstrap.Tooltip.getOrCreateInstance(el, {
                    title: el.dataset.bsConfig || el.dataset.title || '',
                    trigger: (el.dataset.bsTrigger || el.dataset.trigger || 'click') as any
                });

                el.style.cursor = 'pointer';
            });
        }


        // Extend Shariff
        document.querySelectorAll<HTMLElement>('.shariff').forEach((el: HTMLElement) => {
            if(initializationManager.hasElement('shariff', el)) {
                return;
            }
            if(document.querySelector<HTMLElement>('#comments')) {
                let linkContent = '<span class="fas fa-comment"></span> <span class="share_text">kommentieren</span>';
                if(el.querySelector<HTMLElement>('ul')?.classList.contains('icon') || el.querySelector<HTMLElement>('ul')?.classList.contains('icon-count')) {
                    linkContent = '<span class="fas fa-comment"></span>';
                }
                el.querySelector<HTMLElement>('ul')?.prepend(`
                    <li class="shariff-button comment">
                        <a href="#comments" title="kommentieren">${linkContent}</a>
                    </li>
                `);

                el.querySelector<HTMLElement>('li.shariff-button.comment > a')?.addEventListener('click', (ev) => {
                    this.scrollTo('#comments');
                });
            }

            initializationManager.addElement('shariff', el);
        });

        // Set doccheck redirect cookie if needed
        if(typeof(document.cookie) == 'undefined' || document.cookie.indexOf('docchecklogin_redirect=') < 0) {
            if(document.querySelector<HTMLElement>('.dclogin,dc-login-button')) {
                let redirectUrl = window.location.href;
                if(redirectUrl.indexOf('?') > -1) {
                  redirectUrl = redirectUrl.substring(0, redirectUrl.indexOf('?'));
                }
                document.cookie = 'docchecklogin_redirect=' + encodeURIComponent(redirectUrl) + '; max-age=120; path=/; samesite=Lax;';
            }
        }

        // Adjust page main height to sky banner height if needed
        const skyEl: HTMLElement | null = document.querySelector<HTMLElement>('.th-banner-sky .banner-content');
        const containerEl: HTMLElement | null = document.querySelector<HTMLElement>('.th-page-main');
        if(skyEl && containerEl) {
            const diff = skyEl.offsetHeight - (containerEl.offsetTop + containerEl.offsetHeight);
            if(diff > 0) {
                if(containerEl) {
                    containerEl.style.minHeight = (containerEl.offsetHeight + diff) + 'px';
                }
            }
        }

        // Handle initial status of display collapse and dropdown elements
        document.querySelectorAll<HTMLElement>('[data-bs-toggle="collapse"],[data-bs-toggle="dropdown"],[data-bs-toggle="display"]').forEach((el: HTMLElement) => {
            let initialState = el.dataset.initialState ?? el.dataset.bsInitialState ?? el.dataset.initialStatus ?? el.dataset.bsInitialStatus ?? '';
            if (initialState !== '') {
                initialState = ',' + initialState.replace(/ /g, ',') + ',';
                let show = false;
                if (
                    initialState.search(/,open,/i) > -1
                    || initialState.search(/,show,/i) > -1
                ) {
                    show = true;
                }

                if(!show) {
                    [
                        { size: 1, breakPoint: 'sm' },
                        { size: 2, breakPoint: 'md' },
                        { size: 3, breakPoint: 'lg' },
                        { size: 4, breakPoint: 'xl' },
                        { size: 5, breakPoint: 'xxl' }
                    ].forEach((sz) => {
                        if(show) {
                            return false;
                        }
                        if (this.screenSizeInt >= sz.size) {
                            if (initialState.search(new RegExp(',' + sz.breakPoint + '-(open|show),', 'i')) > -1) {
                                show = true;
                            }
                            if (initialState.search(new RegExp(',' + sz.breakPoint + '-(close|hidden),', 'i')) > -1) {
                                show = false;
                            }
                            return false;
                        }
                    })
                }

                const toggle = el.dataset.bsToggle ?? '';
                switch (toggle) {
                    case 'attribute':
                        const attributeToggler: AttributeToggler = AttributeToggler.getOrCreateInstance(el) as AttributeToggler;
                        if (show) {
                            attributeToggler.show();
                        } else {
                            attributeToggler.hide();
                        }
                        break;
                    case 'dropdown':
                        const dropdown = bootstrap.Dropdown.getOrCreateInstance(el);
                        if (show) {
                            dropdown.show();
                        } else {
                            dropdown.hide();
                        }
                        break;
                    case 'collapse':
                        const targetSelector = el.dataset.bsTarget ?? el.getAttribute('href') ?? '';
                        if(targetSelector !== '') {
                            const targetEl = document.querySelector(targetSelector);
                            if(targetEl) {
                                const collapse = bootstrap.Collapse.getOrCreateInstance(targetEl, {
                                    toggle: show
                                });
                                if (show) {
                                    collapse.show();
                                } else {
                                    collapse.hide();
                                }
                            }
                        }
                        break;
                }
            }
        });

        // Justify dropdown menus height
        document.querySelectorAll<HTMLElement>('[data-bs-toggle="dropdown"]').forEach((el: HTMLElement) => {
            if(el.dataset.bsTarget) {
                const dropdownMenuEl: HTMLElement|null = document.querySelector(el.dataset.bsTarget);
                if(dropdownMenuEl) {
                    el.addEventListener('shown.bs.dropdown', () => {
                        let mh = window.innerHeight - 50;
                        const headerEl = document.querySelector<HTMLElement>('.th-page-header');
                        if(headerEl) {
                            mh -= headerEl.offsetHeight;
                        }
                        const footerOverlayEl = document.querySelector<HTMLElement>('.th-page-footer-overlay');
                        if(footerOverlayEl) {
                            mh -= footerOverlayEl.offsetHeight;
                        }
                        if(dropdownMenuEl.offsetHeight > mh) {
                            dropdownMenuEl.style.setProperty('overflow-y', 'auto');
                            dropdownMenuEl.style.setProperty('max-height', mh +  'px');
                        }
                    })
                }
            }
        });

        // Force direct image loading inside carousel
        document.querySelectorAll<HTMLElement>('.carousel img').forEach((el: HTMLElement) => {
            el.removeAttribute('loading');
        });

        // Prepare medias
        this.prepareMedias();
    }

    public showLogin(message: string, redirectUrl: string = ''): void {
        const pageServiceEl: HTMLElement = document.querySelector<HTMLElement>('#th_page_service')!;
        const pageNavMainContainerOffcanvas: bootstrap.Offcanvas = bootstrap.Offcanvas.getOrCreateInstance(pageServiceEl);
        const userTabEl: HTMLElement = document.querySelector<HTMLElement>('#th_page_service_tab_user')!;
        const userTabPaneEl: HTMLElement|null = document.querySelector<HTMLElement>('#th_page_service_user_tabpane');

        if(userTabPaneEl == null) {
            return;
        }

        const userTab: bootstrap.Tab = bootstrap.Tab.getOrCreateInstance(userTabEl);
        pageNavMainContainerOffcanvas.show();
        userTab.show();

        const processLogin = () => {
            const alertInfoEl: HTMLElement|null = userTabPaneEl.querySelector<HTMLElement>('.login-info');
            const userFieldEl: HTMLInputElement|null = userTabPaneEl.querySelector<HTMLInputElement>('input[name="user"]');
            if(alertInfoEl) {
                if(typeof(this.loginAlertInfoDefaultContent) === 'undefined') {
                    this.loginAlertInfoDefaultContent = alertInfoEl.innerHTML;
                }

                if(message === '') {
                    message = this.loginAlertInfoDefaultContent!;
                }

                alertInfoEl.classList.remove('d-none');
                if(message !== '') {
                    alertInfoEl.innerHTML = message;
                } else {
                    alertInfoEl.classList.add('d-none');
                }
            }
            if(userFieldEl) {
                userFieldEl.focus();
            }
            if(redirectUrl != '') {
                let tries = 0;
                let tryInterval: number;
                tryInterval = window.setInterval(() => {
                    const redirectUrlFieldEl:HTMLInputElement = userTabPaneEl.querySelector<HTMLInputElement>('input[name="redirect_url"]')!;
                    if(redirectUrlFieldEl) {
                        redirectUrlFieldEl.value = redirectUrl;
                        tries = 5;
                    }
                    tries++;
                    if(tries > 5) {
                        window.clearInterval(tryInterval);
                    }
                }, 500);
            }
        };

        if(userTabPaneEl && userTabPaneEl.querySelector('.th-fsc-csl')) {
            userTabPaneEl.querySelectorAll<HTMLElement>('.th-fsc-csl').forEach((el: HTMLElement) => {
                const cslEl: CslContainer = CslContainer.getOrCreateInstance<CslContainer>(el);
                if(cslEl.isContentLoaded) {
                    processLogin();
                } else {
                    el.addEventListener('contentLoaded', () => {
                        processLogin();
                    });
                }
            });
        } else {
            processLogin();
        }
    }

    public getScreenSize() {
        return {
            key: this.screenSizeInt,
            size: this.screenSize
        };
    }

    public scrollTo(elOrSelector: string|HTMLElement|null|undefined, options: ScrollToConfig|null = null): void {
        ScrollTo.scrollTo(elOrSelector, options)
    }
}
